import type { ComponentProps } from "react";

type UTMParameters = {
  source?: string;
  medium?: string;
  campaign?: string;
  content?: string;
};

type LaunchExternalStrategy = {
  shouldRenderLink(url: string): boolean;
  shouldLaunchExternally(url: string): boolean;
  getExternalLinkProps(url: string, utm?: UTMParameters): ComponentProps<"a">;
};

// TODO: Handle MLB links
const DEFAULT_LAUNCH_EXTERNAL_STRATEGY: LaunchExternalStrategy = {
  shouldRenderLink: (url) => hasSafeScheme(url),
  shouldLaunchExternally: (url) =>
    hasHTTPScheme(url) && hasDifferentOrigin(url),
  getExternalLinkProps: (url, utm = {}) => ({
    href: hasHTTPScheme(url) ? withUtmParameters(url, utm) : url,
    target: "_blank",
    rel: "noopener noreferrer",
  }),
};

const DEFAULT_UTM_PARAMETERS = {
  source: "pick3",
  campaign: "pick3",
} satisfies UTMParameters;

export function useExternalLinkProps(
  url: string | null,
  {
    utm,
  }: {
    utm: UTMParameters;
  } = { utm: {} },
) {
  if (!url) {
    return null;
  }

  const strategy = DEFAULT_LAUNCH_EXTERNAL_STRATEGY;

  if (!strategy.shouldRenderLink(url)) {
    return null;
  }

  if (strategy.shouldLaunchExternally(url)) {
    return strategy.getExternalLinkProps(url, utm);
  }

  return { href: url };
}

const UNSAFE_PROTOCOLS = [
  "blob:",
  "data:",
  "file:",
  "javascript:",
  "vbscript:",
];

/**
 * Parses a URL string, using `window.location.origin` as the base so that
 * relative URLs (e.g. `/path`, `./path`, `#hash`) resolve correctly instead
 * of throwing. Returns `null` for completely unparseable inputs.
 */
function tryParseURL(href: string): URL | null {
  try {
    return new URL(href, window.location.origin);
  } catch {
    return null;
  }
}

function hasSafeScheme(href: string) {
  const url = tryParseURL(href);
  if (!url) {
    return false;
  }

  return !UNSAFE_PROTOCOLS.includes(url.protocol);
}

function hasHTTPScheme(href: string) {
  const url = tryParseURL(href);
  if (!url) {
    return false;
  }

  return url.protocol === "http:" || url.protocol === "https:";
}

function hasDifferentOrigin(href: string) {
  const url = tryParseURL(href);
  if (!url) {
    return false;
  }

  return url.origin !== window.location.origin;
}

function withUtmParameters(
  href: string,
  utm: {
    source?: string;
    medium?: string;
    campaign?: string;
    content?: string;
  },
) {
  const url = new URL(href);

  const utmWithDefaults = {
    ...DEFAULT_UTM_PARAMETERS,
    ...utm,
  };

  for (const [key, value] of Object.entries(utmWithDefaults)) {
    if (value && !url.searchParams.has(`utm_${key}`)) {
      url.searchParams.append(`utm_${key}`, value);
    }
  }

  return url.toString();
}
